import React, { useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { HologramPortrait } from "@/components/fx/HologramPortrait";
import { TID } from "@/lib/testIds";

/** The compact hero hologram. Hover (or keyboard-focus) expands it into a
 * full-size cinematic version pinned to the viewport, so the centrepiece can
 * live small in the header without losing its detail. Click opens the agent. */
export function HologramHoverReveal() {
    const [open, setOpen] = useState(false);
    const nav = useNavigate();

    return (
        <>
            <button
                data-testid={TID.dashboardHologramTrigger}
                onMouseEnter={() => setOpen(true)}
                onMouseLeave={() => setOpen(false)}
                onFocus={() => setOpen(true)}
                onBlur={() => setOpen(false)}
                onClick={() => nav("/utsav")}
                aria-label="Utsav AI agent — hover to reveal, click to open"
                className="relative rounded-full outline-none transition-transform duration-500 hover:scale-[1.08] focus-visible:ring-2 focus-visible:ring-cyan-400/60"
            >
                <HologramPortrait testId={TID.dashboardHologram} className="w-[68px] h-[68px]" showLabel={false} />
            </button>

            {/* Portalled to <body>: the hero's backdrop-filter would otherwise
              * become the containing block for these fixed layers and clip them. */}
            {createPortal(
                <AnimatePresence>
                {open && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.35 }}
                            className="fixed inset-0 z-40 pointer-events-none bg-[#05050A]/75 backdrop-blur-[3px]"
                        />
                        <motion.div
                            data-testid={TID.dashboardHologramReveal}
                            initial={{ opacity: 0, scale: 0.55, x: 40, y: -40 }}
                            animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
                            exit={{ opacity: 0, scale: 0.6, x: 30, y: -30 }}
                            transition={{ type: "spring", stiffness: 210, damping: 23 }}
                            className="fixed right-12 top-24 z-50 pointer-events-none"
                            style={{ transformOrigin: "top right" }}
                        >
                            <div
                                className="relative p-9 rounded-sm border border-cyan-400/25 bg-[#05050A]/95 backdrop-blur-xl"
                                style={{ boxShadow: "0 0 120px rgb(var(--accent-1-rgb) / 0.22), 0 0 200px rgb(var(--accent-3-rgb) / 0.12)" }}
                            >
                                <HologramPortrait className="w-[300px] h-[300px]" label="UTSAV · AI AGENT" />
                                <p className="mt-10 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                                    Click to open the agent
                                </p>
                            </div>
                        </motion.div>
                    </>
                )}
                </AnimatePresence>,
                document.body
            )}
        </>
    );
}
